/**
 * Client-side narrowing of the visible events.
 *
 * A board carrying voice-agent and platform-agent bookings alongside a rep's
 * own meetings needs a "just mine". The events for the visible period are
 * already in memory (useCalendarEvents fetched the whole range), so filtering
 * here costs nothing and a round trip would only buy a spinner.
 *
 * Pure: no React.
 */
import type { CalendarEvent, CalendarEventSource } from '../../../types/calendar';

export const ALL_EVENT_SOURCES: readonly CalendarEventSource[] = [
  'manual',
  'voice_agent',
  'platform_agent',
  'provider_sync',
];

export interface EventFilter {
  sources: ReadonlySet<CalendarEventSource>;
  hideCancelled: boolean;
}

export const DEFAULT_EVENT_FILTER: EventFilter = {
  sources: new Set(ALL_EVENT_SOURCES),
  // A cancellation is information — the slot just freed up. Hiding it is a
  // deliberate choice, never the default.
  hideCancelled: false,
};

/** `source` is optional on the type by design; a legacy row is a person's entry. */
function sourceOf(event: CalendarEvent): CalendarEventSource {
  return event.source ?? 'manual';
}

export function filterEvents(
  events: readonly CalendarEvent[],
  filter: EventFilter,
): CalendarEvent[] {
  /**
   * An empty selection means "nothing chosen", which the UI reaches by
   * deselecting the last chip — and an empty board reads as broken, not as
   * filtered. The filter narrows; it is not a way to hide everything. (Same
   * trap as an empty allowlist meaning allow-all: absence of a choice is not
   * a choice to exclude.)
   */
  const matchesSource =
    filter.sources.size === 0
      ? () => true
      : (event: CalendarEvent) => filter.sources.has(sourceOf(event));

  return events.filter(
    (event) => matchesSource(event) && !(filter.hideCancelled && event.status === 'cancelled'),
  );
}

export type EventSourceCounts = Record<CalendarEventSource, number>;

/**
 * How many events each source contributes to the visible period, counted
 * BEFORE filtering — so a rep can see that switching a source back on would
 * actually show them something.
 */
export function countEventsBySource(events: readonly CalendarEvent[]): EventSourceCounts {
  const counts = Object.fromEntries(
    ALL_EVENT_SOURCES.map((source) => [source, 0]),
  ) as EventSourceCounts;

  for (const event of events) {
    counts[sourceOf(event)] += 1;
  }

  return counts;
}

export function toggleSource(filter: EventFilter, source: CalendarEventSource): EventFilter {
  const sources = new Set(filter.sources);
  if (!sources.delete(source)) sources.add(source);

  return { ...filter, sources };
}
